import React, { useState, useEffect } from "react";

const FuelCostCalculator = () => {
  const [fuelType, setFuelType] = useState("petrol");
  const [dailyDistance, setDailyDistance] = useState(70);
  const [mileage, setMileage] = useState(15);
  const [fuelCost, setFuelCost] = useState(100);
  const [result, setResult] = useState({
    dailyFuelCost: "0.00",
    monthlyFuelCost: "0.00",
    yearlyFuelCost: "0.00",
    dailyFuelConsumption: "0.00",
  });

  // Fuel Cost Calculation Function
  const calculateFuelCost = () => {
    const distance = parseFloat(dailyDistance);
    const mileageValue = parseFloat(mileage);
    const costPerLiter = parseFloat(fuelCost);

    if (isNaN(distance) || isNaN(mileageValue) || isNaN(costPerLiter) || distance <= 0 || mileageValue <= 0 || costPerLiter <= 0) return;

    // Calculate daily fuel consumption in liters
    const dailyFuelConsumption = distance / mileageValue;
    
    // Calculate costs
    const dailyCost = dailyFuelConsumption * costPerLiter;
    const monthlyCost = dailyCost * 30;
    const yearlyCost = dailyCost * 365;

    setResult({
      dailyFuelCost: dailyCost.toFixed(2),
      monthlyFuelCost: monthlyCost.toFixed(2),
      yearlyFuelCost: yearlyCost.toFixed(2),
      dailyFuelConsumption: dailyFuelConsumption.toFixed(2),
    });
  };

  // Run default calculation on first load
  useEffect(() => {
    calculateFuelCost();
  }, []);

  const monthlyLiters = (Number(result.dailyFuelConsumption) * 30).toFixed(2);
  const yearlyLiters = (Number(result.dailyFuelConsumption) * 365).toFixed(2);

  return (
    <div className="calculator-wrapper">
      <div className="calculator-container">
        {/* Header */}
        <div className="calculator-header">
          <div className="header-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: 'white'}}>
              <path d="M3 22h18"></path>
              <path d="M4 9h10"></path>
              <path d="M12 7v14"></path>
              <path d="M8 19v-4a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v4"></path>
            </svg>
          </div>
          <h1>Fuel Cost Calculator</h1>
          <p>
            Calculate your daily, monthly, and yearly fuel expenses based on distance traveled and vehicle mileage
          </p>
        </div>

        {/* Main Calculator Card */}
        <div className="calculator-card">
          <div className="calculator-grid">
            {/* Left Side - Input Section */}
            <div className="input-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 22h18"></path>
                  <path d="M4 9h10"></path>
                  <path d="M12 7v14"></path>
                  <path d="M8 19v-4a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v4"></path>
                </svg>
                Fuel Details
              </h2>

              {/* Fuel Type */}
              <div className="input-group">
                <label>Fuel Type</label>
                <div className="quick-select-group">
                  <button
                    onClick={() => setFuelType("petrol")}
                    className={`quick-select-btn ${fuelType === "petrol" ? "primary" : "secondary"}`}
                  >
                    Petrol
                  </button>
                  <button
                    onClick={() => setFuelType("diesel")}
                    className={`quick-select-btn ${fuelType === "diesel" ? "primary" : "secondary"}`}
                  >
                    Diesel
                  </button>
                </div>
              </div>

              {/* Daily Distance */}
              <div className="input-group">
                <label>Daily Distance Travelled</label>
                <div className="input-wrapper">
                  <input
                    type="number"
                    value={dailyDistance}
                    onChange={(e) => setDailyDistance(e.target.value)}
                    className="calc-input"
                    placeholder="70"
                  />
                  <span className="input-suffix">KM</span>
                </div>
                <div className="quick-select-group">
                  {[30, 50, 70, 100].map((distance) => (
                    <button
                      key={distance}
                      onClick={() => setDailyDistance(distance)}
                      className="quick-select-btn primary"
                    >
                      {distance} KM
                    </button>
                  ))}
                </div>
              </div>

              {/* Mileage */}
              <div className="input-group">
                <label>Mileage</label>
                <div className="input-wrapper">
                  <input
                    type="number"
                    value={mileage}
                    onChange={(e) => setMileage(e.target.value)}
                    className="calc-input"
                    placeholder="15"
                    step="0.1"
                  />
                  <span className="input-suffix">KM/LTR</span>
                </div>
                <div className="quick-select-group">
                  {[10, 15, 20, 25].map((km) => (
                    <button
                      key={km}
                      onClick={() => setMileage(km)}
                      className="quick-select-btn secondary"
                    >
                      {km} KM/L
                    </button>
                  ))}
                </div>
              </div>

              {/* Fuel Cost */}
              <div className="input-group">
                <label>Fuel Cost</label>
                <div className="input-wrapper">
                  <span className="input-prefix">₹</span>
                  <input
                    type="number"
                    value={fuelCost}
                    onChange={(e) => setFuelCost(e.target.value)}
                    className="calc-input"
                    placeholder="100"
                    step="0.1"
                  />
                  <span className="input-suffix">/L</span>
                </div>
                <div className="quick-select-group">
                  {[90, 100, 110, 120].map((cost) => (
                    <button
                      key={cost}
                      onClick={() => setFuelCost(cost)}
                      className="quick-select-btn success"
                    >
                      ₹{cost}/L
                    </button>
                  ))}
                </div>
              </div>

              <button onClick={calculateFuelCost} className="calculate-btn">
                Calculate Fuel Cost
              </button>
            </div>

            {/* Right Side - Results Section */}
            <div className="result-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <circle cx="12" cy="12" r="6"></circle>
                  <circle cx="12" cy="12" r="2"></circle>
                </svg>
                Cost Summary
              </h2>

              <div className="result-card">
                {/* Daily Fuel Cost */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Daily Fuel Cost</span>
                    <div className="result-icon-wrapper blue">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#3b82f6'}}>
                        <line x1="12" y1="1" x2="12" y2="23"></line>
                        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.dailyFuelCost).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    {result.dailyFuelConsumption} liters per day
                  </div>
                </div>

                {/* Monthly Fuel Cost - Highlighted */}
                <div className="result-item highlight">
                  <div className="result-item-header">
                    <span className="result-label">Monthly Fuel Cost</span>
                    <div className="result-icon-wrapper white">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: 'white'}}>
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                        <line x1="16" y1="2" x2="16" y2="6"></line>
                        <line x1="8" y1="2" x2="8" y2="6"></line>
                        <line x1="3" y1="10" x2="21" y2="10"></line>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.monthlyFuelCost).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    {monthlyLiters} liters per month
                  </div>
                </div>

                {/* Yearly Fuel Cost */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Yearly Fuel Cost</span>
                    <div className="result-icon-wrapper green">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#10b981'}}>
                        <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"></polyline>
                        <polyline points="16 7 22 7 22 13"></polyline>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.yearlyFuelCost).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    {yearlyLiters} liters per year
                  </div>
                </div>

                {/* Visual Breakdown */}
                <div className="breakdown-section">
                  <h3 className="breakdown-title">Cost Breakdown</h3>
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Daily Cost</span>
                      <span className="breakdown-percentage">
                        {((Number(result.dailyFuelCost) / Number(result.monthlyFuelCost)) * 100).toFixed(1)}%
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill blue"
                        style={{
                          width: `${(Number(result.dailyFuelCost) / Number(result.monthlyFuelCost)) * 100}%`,
                        }}
                      />
                    </div>
                  </div>
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Monthly Cost</span>
                      <span className="breakdown-percentage green">
                        100%
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill green"
                        style={{
                          width: '100%',
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Info Section */}
        <div className="info-section">
          <div className="info-header">
            <div className="info-icon-wrapper">
              <svg className="info-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="16" x2="12" y2="12"></line>
                <line x1="12" y1="8" x2="12.01" y2="8"></line>
              </svg>
            </div>
            <div>
              <h3>How Does the Fuel Cost Calculator Work?</h3>
              <p>
                The fuel cost calculator estimates your daily, monthly, and yearly fuel expenses based 
                on your vehicle's mileage and the distance you travel. It divides your daily distance 
                by your vehicle's mileage to determine fuel consumption, then multiplies by the fuel 
                price to calculate costs. This helps you budget and track your transportation expenses.
              </p>
            </div>
          </div>

          <div className="formula-section">
            <h4>Formula Used:</h4>
            <div className="formula-display">
              Daily Cost = (Distance ÷ Mileage) × Fuel Price
            </div>

            <div className="formula-params">
              <div className="formula-param">
                <div className="formula-param-key">Distance</div>
                <div className="formula-param-value">Daily distance travelled in KM</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Mileage</div>
                <div className="formula-param-value">Vehicle mileage in KM/Liter</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Fuel Price</div>
                <div className="formula-param-value">Current fuel cost per liter</div>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="faq-section">
            <h3>Frequently Asked Questions</h3>

            <div className="faq-item">
              <h5>How accurate is the fuel cost calculator?</h5>
              <p>
                The calculator provides estimates based on the inputs you provide. Actual fuel consumption 
                may vary depending on driving conditions, traffic, vehicle maintenance, driving habits, 
                and terrain. For best accuracy, use your vehicle's real-world mileage figures and 
                current local fuel prices.
              </p>
            </div>

            <div className="faq-item">
              <h5>What factors affect vehicle mileage?</h5>
              <p>
                Vehicle mileage is affected by driving style (aggressive acceleration reduces mileage), 
                vehicle maintenance (clean air filters, proper tire pressure), traffic conditions 
                (stop-and-go traffic reduces efficiency), load weight, air conditioning usage, and 
                terrain (hills reduce mileage). Regular servicing helps maintain optimal fuel efficiency.
              </p>
            </div>

            <div className="faq-item">
              <h5>Should I choose petrol or diesel?</h5>
              <p>
                Diesel vehicles typically offer better mileage (15-30% more) and are more economical 
                for high daily distances (over 50-60 km). However, diesel vehicles have higher initial 
                costs and maintenance expenses. Petrol is better for city driving with lower daily 
                distances. Consider your driving patterns and long-term usage before deciding.
              </p>
            </div>

            <div className="faq-item">
              <h5>How can I improve my vehicle's fuel efficiency?</h5>
              <p>
                Maintain steady speeds, avoid aggressive acceleration and braking, keep tires properly 
                inflated, remove excess weight from your vehicle, use air conditioning judiciously, 
                get regular vehicle servicing, use the recommended engine oil, and plan routes to 
                avoid traffic congestion. These habits can improve mileage by 10-25%.
              </p>
            </div>

            <div className="faq-item">
              <h5>How do fuel prices vary in India?</h5>
              <p>
                Fuel prices in India are revised daily based on international crude oil prices and 
                exchange rates. Prices vary by state due to different VAT rates and local taxes. 
                Metro cities generally have higher prices. You can check current rates on oil company 
                websites or mobile apps. Planning fuel stops in lower-tax states during long trips 
                can provide savings.
              </p>
            </div>

            <div className="faq-item">
              <h5>Is it worth switching to CNG or electric vehicles?</h5>
              <p>
                CNG offers 40-50% cost savings over petrol but requires infrastructure availability 
                and has lower boot space. Electric vehicles have minimal running costs (₹1-2/km) but 
                higher upfront costs and limited charging infrastructure. For daily city commutes 
                over 50 km, CNG or EVs can recover their additional costs within 3-5 years through 
                fuel savings.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FuelCostCalculator;